import {type ComponentProps, useEffect, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useMutation} from '@tanstack/react-query';
import {LocalStorage} from '@chekin/core';
import {API} from '@chekin/api';
import {RESERVATION_ID_KEY} from 'utils/constants';
import {createSession} from 'components/sessions/utils';
import {useChekinSDK} from 'context/ChekinSDK';
import {SearchingBookingModal} from '../../components/SearchingBookingModal';

type SearchingBookingStatus = ComponentProps<typeof SearchingBookingModal>['status'];

const STATUS_MODAL_STATES = {
  SUCCESS: 'SUCCESS' as SearchingBookingStatus,
  LOADING: 'LOADING' as SearchingBookingStatus,
  IDLE: 'IDLE' as SearchingBookingStatus,
} as const;

type QueryParams = {
  check_in_date: string;
  check_out_date: string;
  known_number_of_guests: string;
  default_lead_guest_email: string;
  default_lead_guest_name?: string;
  external_id?: string;
};

const parseQueryParams = (searchParams: URLSearchParams): QueryParams | null => {
  const check_in_date = searchParams.get('check_in_date');
  const check_out_date = searchParams.get('check_out_date');
  const known_number_of_guests = searchParams.get('known_number_of_guests');
  const default_lead_guest_email = searchParams.get('default_lead_guest_email');

  if (
    !check_in_date ||
    !check_out_date ||
    !known_number_of_guests ||
    !default_lead_guest_email
  ) {
    return null;
  }

  return {
    check_in_date,
    check_out_date,
    known_number_of_guests,
    default_lead_guest_email,
    default_lead_guest_name: searchParams.get('default_lead_guest_name') ?? undefined,
    external_id: searchParams.get('external_id') ?? undefined,
  };
};

function useDirectReservation() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {isSuccessSDK} = useChekinSDK();
  const hasStarted = useRef(false);

  const [modalStatus, setModalStatus] = useState<SearchingBookingStatus>(
    STATUS_MODAL_STATES.LOADING,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const params = parseQueryParams(searchParams);

  const redirectToReservation = (reservationId: string, signUpFormLink?: string) => {
    createSession({reservationId});
    LocalStorage.set(RESERVATION_ID_KEY, reservationId);

    if (signUpFormLink) {
      const path = new URL(signUpFormLink).pathname;
      navigate(path, {replace: true});
    }

    setModalStatus(STATUS_MODAL_STATES.SUCCESS);
  };

  const {mutate: getOrCreateDirectReservation} = useMutation({
    mutationFn: (queryParams: QueryParams) =>
      API.reservation.getOrCreateDirectReservation(queryParams),
    onSuccess: data => {
      if (data?.id) {
        redirectToReservation(data.id, data.signup_form_link);
      } else {
        setModalStatus(STATUS_MODAL_STATES.IDLE);
        setErrorMessage(t('something_went_wrong'));
      }
    },
    onError: () => {
      setModalStatus(STATUS_MODAL_STATES.IDLE);
      setErrorMessage(t('something_went_wrong'));
    },
  });

  useEffect(
    function startSearchOnLoad() {
      if (hasStarted.current || !params || !isSuccessSDK) return;

      hasStarted.current = true;
      getOrCreateDirectReservation(params);
    },
    [params, isSuccessSDK, getOrCreateDirectReservation],
  );

  return {params, modalStatus, errorMessage};
}

export {useDirectReservation};
